/**
 * StandaloneSchedulePage.jsx
 * ===========================
 * Hosts the TransferWizard outside of ProjectDetails so QA (and PMs who
 * want a focused workflow) can reach it without the parent page's heavy
 * initial-load API storm.
 *
 * Route: /project/:projectId/schedule-wizard
 *
 * Loads ONLY the project document (name + units + address) — nothing else.
 */
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Loader2 } from 'lucide-react';
import TransferWizard from '@/components/TransferWizard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StandaloneSchedulePage({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/projects/${projectId}`);
        if (!cancelled) setProject(res.data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Failed to load project');
      }
    })();
    return () => { cancelled = true; };
  }, [projectId]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-xl border border-rose-200 p-6 max-w-md">
          <h2 className="text-base font-semibold text-rose-900 mb-1">Couldn't load project</h2>
          <p className="text-sm text-slate-600">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-slate-100 hover:bg-slate-200"
            data-testid="standalone-wizard-error-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <TransferWizard
        project={project}
        userId={user?.id}
        onClose={() => navigate(`/project/${project.id}`)}
        onLocked={() => { /* user clicks Done to dismiss */ }}
      />
    </div>
  );
}
